import { Controller } from "@hotwired/stimulus";
import { createFlow, skip, branches, safe } from "@/controllers/flow";
import { InitializeTask, PlayButtonTask, StartGameTask, ToSPPageTask, isSP, isTB } from "./play/flow_game_shared";
import { AdsPreloadTask, AdsConfirmTask, AdsWatchTask, AdsCompleteTask } from "./play/flow_game_ads";
import { CIDConfirmTask } from "./play/flow_game_cid";

/**
 * クラシックゲーム（ブラウザゲーム）の起動フローを組み立てて実行する controller。
 *
 * values:
 * - needLoginModal: CIDログイン確認モーダルが必要か
 * - needAdsModal: 広告視聴フローが必要か
 * - skipLoadGame: ゲームロードをスキップ（＝モーダルを省略）するか
 * - redirectPath: SP/TB の場合に遷移するURL
 *
 * フロー概要:
 * - 初期化 → (必要に応じて) 広告/CID/スキップ分岐 → PCならゲーム開始、SP/TBなら別ページへ
 */
export default class extends Controller {
  static values = {
    needLoginModal: Boolean,
    needAdsModal: Boolean,
    skipLoadGame: Boolean,
    redirectPath: String
  };

  connect() {
    const BRANCH_SKIP_MODAL = "SkipModal";
    const BRANCH_CID_MODAL = "CIDSkipModal";
    const BRANCH_ADS_MODAL = "AdsModal";
    const BRANCH_NO_MODAL = "NoModal";
    const selectModal = () => {
      const isNotPlayable = !document.querySelector("#game-loader");

      if (isNotPlayable) return undefined;
      if (this.needAdsModalValue) return BRANCH_ADS_MODAL;
      if (this.skipLoadGameValue) return BRANCH_SKIP_MODAL;
      if (this.needLoginModalValue) return BRANCH_CID_MODAL;
      return BRANCH_NO_MODAL;
    };

    const BRANCH_PC = "PC";
    const BRANCH_SP = "SP";
    const selectBoot = () => isSP() || isTB() ? BRANCH_SP : BRANCH_PC;

    createFlow()
      // .debug()
      .flow(InitializeTask)
      .flow(
        branches(selectModal)
          .branch(BRANCH_SKIP_MODAL, skip())
          .branch(BRANCH_NO_MODAL, PlayButtonTask)
          .branch(BRANCH_ADS_MODAL,
            safe()
              .flow(AdsPreloadTask)
              .flow(PlayButtonTask)
              .flow(AdsConfirmTask)
              .flow(AdsWatchTask)
              .flow(AdsCompleteTask))
          .branch(BRANCH_CID_MODAL,
            safe()
              .flow(PlayButtonTask)
              .flow(CIDConfirmTask)))
      .flow(
        branches(selectBoot)
          .branch(BRANCH_PC, StartGameTask)
          .branch(BRANCH_SP, ToSPPageTask, this.redirectPathValue))
      .run();

    // bfcache対策: Safariでブラウザバックで復元された際にキャッシュを使わずリロードする
    this.onPageShow = (event) => {
      if (event.persisted) location.reload();
    };
    window.addEventListener("pageshow", this.onPageShow);
  }

  disconnect() {
    window.removeEventListener("pageshow", this.onPageShow);
  }
}
